import { type ItemResponse, WIZARD_STEP_COUNTS } from '@finn-no/mobility-item-insertion-shared';
import pulse from '@finn-no/pulse-sdk';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Clickable, DeadToggle, Modal } from '@warp-ds/react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { LookupCfg } from '../../../types/config.js';
import { LookupWizardNavigation } from '../../components/vehicle-lookup/LookupWizardNavigation.js';
import { lookupTitle } from '../../helpers/lookup-messages.js';
import { redirectToItemInsertion } from '../../helpers/vehicle-lookup-redirect.js';
import { useLookupPulse } from '../../hooks/useLookupPulse.js';
import { createItemFromVehicleLookup, updateItemFromVehicleLookup } from '../../services/vehicle-item-creation.js';
import { lookupVehicle } from '../../services/vehicle-lookup.js';

type VariantSelectionPageProps = {
  config: LookupCfg;
};

type VariantCardProps = {
  variant: { id: number; name: string } | 'other';
  isSelected: boolean;
  onClick: () => void;
  isDisabled: boolean;
  label: string;
  showError?: boolean;
};

const VariantCard = ({ variant, isSelected, onClick, isDisabled, label, showError }: VariantCardProps) => {
  const variantId = variant === 'other' ? 'other' : variant.id;
  const errorClasses = showError ? 's-border-negative border-1 rounded-8' : '';

  return (
    <div className={errorClasses}>
      <Card
        selected={isSelected}
        className={`p-16 ${isDisabled ? 'pointer-events-none' : ''}`}
        data-testid={`variant-card-${variantId}`}
      >
        <div className="flex gap-12 items-center">
          <DeadToggle radio checked={isSelected} className="-mt-8" />
          <Clickable onClick={isDisabled ? undefined : onClick}>{label}</Clickable>
        </div>
      </Card>
    </div>
  );
};

export const VariantSelectionPage = ({ config }: VariantSelectionPageProps) => {
  const { i18n } = useLingui();
  const { trackClick } = useLookupPulse(config.model);
  const { regnr, variantId, itemId: routeItemId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgId = params.get('orgId') ?? undefined;
  const mileage = params.get('mileage') ?? undefined;

  const buildQueryParams = (): Record<string, string> => {
    const queryParams: Record<string, string> = { model: config.model };
    if (orgId) queryParams.orgId = orgId;
    if (mileage) queryParams.mileage = mileage;
    return queryParams;
  };

  const [submitError, setSubmitError] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showSearchAgainModal, setShowSearchAgainModal] = useState<boolean>(false);
  const [skipNextConfirm, setSkipNextConfirm] = useState<boolean>(false);
  const [pendingVariantId, setPendingVariantId] = useState<string | null>(null);

  // Determine if we're in the new flow (TAI-1899) - item already exists
  const effectiveItemId = routeItemId || config.itemId;
  const isNewFlow = Boolean(effectiveItemId);
  const variantErrorId = 'variant-selection-error';

  // Check if the selection has changed from what's already stored on the item
  const storedVariantId = config.item?.data?.vehicle_lookup?.variant_id;
  const storedRegnr = config.item?.data?.registration_number;
  const hasSelectionChanged = () => {
    if (!isNewFlow || !config.item?.etag) return true; // Always update in legacy flow
    // If regnr or variantId differs from stored values, selection has changed
    const regnrChanged = regnr?.toUpperCase() !== storedRegnr?.toUpperCase();
    const variantChanged = variantId !== storedVariantId;
    return regnrChanged || variantChanged;
  };

  const VARIANT_OPTION_OTHER = 'other';

  // Helper to build lookup URLs with proper encoding
  const buildLookupUrl = (options: {
    basePath: string;
    regnr?: string;
    variantId?: string;
    queryParams: Record<string, string>;
  }) => {
    let path = options.basePath;
    if (options.regnr) {
      path += `/${encodeURIComponent(options.regnr)}`;
      if (options.variantId) {
        path += `/${encodeURIComponent(options.variantId)}`;
      }
    }
    const query = new URLSearchParams(options.queryParams);
    return query.toString() ? `${path}?${query}` : path;
  };

  // Variants and summary are only returned when looking up REGNR, so we need two separate queries
  const { isLoading, isSuccess, data } = useQuery({
    queryKey: ['regnr', regnr],
    queryFn: () =>
      lookupVehicle({
        regnr,
        csrfToken: config.csrfToken,
        root: config.root,
        model: config.model,
        isCategoryMismatchEnabled: config.isCategoryMismatchEnabled,
      }),
    enabled: !!regnr,
  });

  const { summary, variants, ...lookupCreationData } = data ?? {};
  const hasVariants = isSuccess && data?.variants && data.variants.length > 0;
  const hasVariantId = !!variantId && variantId !== '' && variantId !== VARIANT_OPTION_OTHER;

  const {
    isLoading: isVariantLoading,
    data: variantData,
    error: variantError,
  } = useQuery({
    queryKey: ['variant', regnr, variantId],
    queryFn: () =>
      lookupVehicle({
        regnr,
        variantId,
        csrfToken: config.csrfToken,
        root: config.root,
        model: config.model,
        isCategoryMismatchEnabled: config.isCategoryMismatchEnabled,
      }),
    enabled: !!regnr && hasVariantId,
  });

  // We remove summary and variants to be sure, even though we don't expect to see them in the variantData
  const { summary: _summary, variants: _variants, ...variantCreationData } = variantData ?? {};

  // Extract stored variant info from item for field reset calculation
  // Use vehicle_lookup.variant_id which is populated by vehicle-lookup-service
  const storedVariantIdFromLookup = config.item?.data?.vehicle_lookup?.variant_id;

  // Fetch stored variant data (variant A) for comparison when changing variants
  // This allows us to identify fields that need to be nullified when switching to a different variant
  const { data: storedVariantResponse } = useQuery({
    queryKey: ['storedVariant', storedRegnr, storedVariantIdFromLookup],
    queryFn: () =>
      lookupVehicle({
        regnr: storedRegnr,
        variantId: storedVariantIdFromLookup,
        csrfToken: config.csrfToken,
        root: config.root,
        model: config.model,
        isCategoryMismatchEnabled: config.isCategoryMismatchEnabled,
      }),
    // Only fetch when we're in the new flow with an existing item that has a stored variant
    enabled: isNewFlow && !!storedRegnr && !!storedVariantIdFromLookup,
    staleTime: Number.POSITIVE_INFINITY, // Cache indefinitely - stored variant data won't change
  });

  // Extract vehicle data from stored variant (excluding summary/variants)
  const {
    summary: _storedSummary,
    variants: _storedVariants,
    ...storedVariantCreationData
  } = storedVariantResponse ?? {};

  const createOrUpdateMutation = useMutation<ItemResponse | null, Error, { skipUpdate?: boolean; mileage?: string }>({
    mutationFn: async ({ skipUpdate, mileage }) => {
      const parsedMileageValue = Number(mileage);
      const parsedMileage =
        mileage && Number.isFinite(parsedMileageValue) && parsedMileageValue > 0
          ? { mileage: Math.round(parsedMileageValue) }
          : {};
      if (isNewFlow && effectiveItemId && config.item?.etag) {
        // New flow (TAI-1899): Update existing item with vehicle data
        if (skipUpdate) {
          // Skip without updating - just return null, we'll navigate in onSuccess
          return null;
        }
        // When user selects "other", also nullify vehicle_lookup.variant_id
        const lookupData = lookupCreationData as Record<string, unknown>;
        const vehicleData = hasVariantId
          ? variantCreationData
          : {
              ...lookupData,
              vehicle_lookup: {
                ...(lookupData.vehicle_lookup as Record<string, unknown> | undefined),
                variant_id: null,
              },
            };

        // Pass previous variant data for field reset calculation when selection has changed
        // This allows us to nullify fields that existed in the previous variant but not in the new one
        const previousData =
          hasSelectionChanged() && Object.keys(storedVariantCreationData).length > 0
            ? storedVariantCreationData
            : undefined;

        return updateItemFromVehicleLookup(
          effectiveItemId,
          config.model,
          { ...vehicleData, ...parsedMileage },
          config.csrfToken,
          config.root,
          config.item.etag,
          orgId,
          previousData,
        );
      }

      // Legacy flow: Create new item
      const vehicleData = skipUpdate ? {} : hasVariantId ? variantCreationData : lookupCreationData;
      return createItemFromVehicleLookup(config.model, { ...vehicleData, ...parsedMileage }, orgId);
    },
    onSuccess: (itemData) => {
      // Handle redirect using shared helper
      redirectToItemInsertion({
        itemData,
        effectiveItemId,
        orgId,
        config: {
          root: config.root,
          isBackNavigationEnabled: config.isBackNavigationEnabled,
          isWizardEnabled: config.isWizardEnabled,
          model: config.model,
        },
        regnr,
        variantId,
      });
    },
  });

  const isLoadingState =
    isLoading || isVariantLoading || createOrUpdateMutation.isPending || createOrUpdateMutation.isSuccess;

  const subtitle = isLoading
    ? i18n.t({
        id: 'mobility-item-insertion.car-info-page.loading',
        comment:
          'Subtitle telling the user that we are currently looking up their registration number for car information',
        message: 'We are looking up details about {regNr}. Please wait...',
        values: { regNr: regnr },
      })
    : isSuccess
      ? i18n.t({
          id: 'mobility-item-insertion.vehicle-info-page.subtitle',
          comment: 'Subtitle instructing users to verify the vehicle brand (car, atv, MC, etc.) before proceeding',
          message: "We found a {vehicleModel}. Make sure it's the correct brand and model before moving on.",
          values: { vehicleModel: data?.summary ?? 'N/A' },
        })
      : i18n.t({
          id: 'mobility-item-insertion.car-info-page.not-found',
          comment: 'Subtitle indicating we were unable to find information about the vehicle',
          message: 'We were unable to find any information about {regNr}. Please try again.',
          values: { regNr: regnr },
        });

  const handleFetchDetails = () => {
    trackClick({
      intent: 'Register',
      name: 'Car lookup with regnumber',
      object: { elementType: 'Link' },
    });

    if (hasVariants && !variantId) {
      setSubmitError(true);
      return;
    }

    // Skip the API call if selection hasn't changed
    // Note: Confirmation modal is now shown immediately on variant selection change (not here)
    const skipUpdate = !hasSelectionChanged();
    createOrUpdateMutation.mutate({ skipUpdate, mileage });
  };

  const handleConfirmUpdate = () => {
    setShowConfirmModal(false);
    if (pendingVariantId) {
      // Navigate to the new variant URL (this will trigger the variant query)
      const basePath = effectiveItemId ? `/item/${effectiveItemId}/lookup` : '/lookup';

      navigate(
        buildLookupUrl({
          basePath,
          regnr,
          variantId: pendingVariantId,
          queryParams: buildQueryParams(),
        }),
      );
      setPendingVariantId(null);
    } else {
      // Fallback for direct "Next" button confirmation (if still needed)
      createOrUpdateMutation.mutate({ skipUpdate: false, mileage });
    }
  };

  const handleCancelUpdate = () => {
    setShowConfirmModal(false);
    setPendingVariantId(null);
  };

  // Check if selecting a new variant would change from the stored value (for existing items)
  const wouldChangeFromStored = (newVariantId: string) => {
    if (!storedVariantId || !config.item?.etag) return false;
    // If the new variant is different from what's stored on the item, it's a change
    return newVariantId !== storedVariantId;
  };

  // Handle "Search again" click - show modal if item exists, otherwise navigate directly
  const handleSearchAgain = () => {
    if (isNewFlow && config.item?.etag) {
      setShowSearchAgainModal(true);
    } else {
      // No item yet, navigate directly
      navigate(
        buildLookupUrl({
          basePath: '/lookup',
          queryParams: buildQueryParams(),
        }),
      );
    }
  };

  // Confirm search again and navigate with flag
  const handleConfirmSearchAgain = () => {
    setShowSearchAgainModal(false);
    setSkipNextConfirm(true);

    const basePath = effectiveItemId ? `/item/${effectiveItemId}/lookup` : '/lookup';
    navigate(
      buildLookupUrl({
        basePath,
        queryParams: buildQueryParams(),
      }),
    );
  };

  const handleCancelSearchAgain = () => {
    setShowSearchAgainModal(false);
  };

  const handleCardVariantSelect = (selectedVariantId: number | string) => {
    setSubmitError(false);
    const selectedId = String(selectedVariantId);

    pulse.trackEvent({
      name: 'Variant selected',
      type: 'Click',
      intent: 'Open',
      object: {
        type: 'UIElement',
        elementType: 'Card',
        id: 'variantCardSelect',
        selectedVariant: selectedId,
      },
      vertical: {
        name: 'mobility',
      },
    });

    // Show confirmation modal if this would change from the stored variant
    // Skip modal if user has already confirmed via "Search again"
    if (!skipNextConfirm && wouldChangeFromStored(selectedId)) {
      setPendingVariantId(selectedId);
      setShowConfirmModal(true);
      return;
    }

    // Clear the skip flag after use
    setSkipNextConfirm(false);

    // Use item-scoped route in new flow (TAI-1899)
    const basePath = effectiveItemId ? `/item/${effectiveItemId}/lookup` : '/lookup';

    navigate(
      buildLookupUrl({
        basePath,
        regnr,
        variantId: selectedId,
        queryParams: buildQueryParams(),
      }),
    );
  };

  const showSelect = (data?.variants?.length ?? 0) > 0;

  const variantErrorMessage = submitError
    ? i18n.t({
        id: 'mobility-item-insertion.vehicle-variant.none-chosen-error',
        comment:
          'Error message shown if the user tries to submit using the Fetch details button without choosing a variant. Other refers to mobility-item-insertion.car-info-page.select.other.option',
        message: "Select a variant or choose 'Other' if yours is not listed",
      })
    : undefined;

  const getErrorMessage = () => {
    switch (true) {
      case !!variantError:
        return i18n.t({
          id: 'vehicle-lookup.variant-fetch-error',
          comment: 'Message shown when fetching vehicle variant details fails, shown as an error message',
          message: 'Failed to fetch variant details. Please try again.',
        });
      case !!createOrUpdateMutation.error:
        return i18n.t({
          id: 'vehicle-lookup.create-item-error',
          comment: 'Message shown when creating an ad fails, shown as an error message',
          message: 'Failed to create ad. Please try again.',
        });
      default:
        return undefined;
    }
  };

  const errorMessage = getErrorMessage();

  const displayStep = 1;
  const stepCount = WIZARD_STEP_COUNTS[config.model as keyof typeof WIZARD_STEP_COUNTS] ?? 0;
  const displayTotal = stepCount + 1; // +1 for vehicle-lookup step

  return (
    <main className={`!mb-32 max-w-[700] mx-auto ${errorMessage || submitError ? 'pb-[164]' : 'pb-[130]'}`}>
      {config.isWizardEnabled ? (
        <header>
          <div className="t4 mb-4">
            <Trans
              id="mobility-item-insertion.form.wizard.progress"
              comment="Indicates the current step and total steps in the wizard"
            >
              Step {displayStep} of {displayTotal}
            </Trans>
          </div>
          <h1 className="mb-16">{lookupTitle(config.model)}</h1>
        </header>
      ) : (
        <h1>{lookupTitle(config.model)}</h1>
      )}
      <p className="mb-32">{subtitle}</p>
      {isSuccess && (
        <>
          <h2 tabIndex={-1} className="t3">
            {regnr}
          </h2>
          <p className="mb-16">{summary}</p>
          <form>
            {showSelect && (
              <fieldset aria-describedby={submitError ? variantErrorId : undefined}>
                <legend className="text-s font-bold pb-16">
                  <Trans id="mobility-item-insertion.car-info-page.select.label">Choose variant</Trans>
                </legend>
                <div className="grid gap-12" data-testid="variant-card-grid">
                  {variants?.map((variant) => (
                    <VariantCard
                      key={variant.id}
                      variant={variant}
                      isSelected={variantId === String(variant.id)}
                      onClick={() => handleCardVariantSelect(variant.id)}
                      isDisabled={isLoading || isVariantLoading}
                      label={variant?.name ? variant.name : (summary ?? '')}
                      showError={submitError && !variantId}
                    />
                  ))}
                  <VariantCard
                    variant="other"
                    isSelected={variantId === VARIANT_OPTION_OTHER}
                    onClick={() => handleCardVariantSelect(VARIANT_OPTION_OTHER)}
                    isDisabled={isLoading || isVariantLoading}
                    label={i18n.t({
                      id: 'mobility-item-insertion.car-info-page.select.other.option',
                      comment: 'Option for users who do not know or cannot find their car variant',
                      message: "Other / Don't know",
                    })}
                    showError={submitError && !variantId}
                  />
                </div>
                {submitError && (
                  <p id={variantErrorId} className="mt-8 s-text-negative text-s">
                    {variantErrorMessage}
                  </p>
                )}
              </fieldset>
            )}
            {!config.isWizardEnabled && (
              <Button
                data-testid="car-variant-submit"
                className={showSelect ? 'mt-32' : undefined}
                primary
                loading={isLoadingState}
                onClick={() => handleFetchDetails()}
              >
                <Trans
                  id="mobility-item-insertion.car-info-page.fetch-details.button"
                  comment="Button text for fetching car variant details"
                >
                  Fetch details
                </Trans>
              </Button>
            )}
            {!!errorMessage && <p className="mt-16 s-text-negative">{errorMessage}</p>}
          </form>
        </>
      )}
      <div className="flex flex-col gap-8 mt-32">
        <Button link onClick={handleSearchAgain}>
          <Trans
            id="mobility-item-insertion.car-info-page.search-again.button"
            comment="Button text for initiating a new search for car variants"
          >
            Search again
          </Trans>
        </Button>
      </div>
      {config.isWizardEnabled && (
        <LookupWizardNavigation
          variant="next"
          onNext={() => {
            handleFetchDetails();
          }}
          displayStep={displayStep}
          displayTotal={displayTotal}
          isLoading={isLoadingState}
          errorMessage={
            variantError ? (
              <span>
                <Trans
                  id="vehicle-lookup.variant-fetch-error.with-link"
                  comment="Error message when variant fetch fails, with clickable link to scroll to variant selection"
                >
                  Failed to fetch{' '}
                  <Button
                    link
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    variant
                  </Button>{' '}
                  details. Please try again.
                </Trans>
              </span>
            ) : createOrUpdateMutation.error ? (
              <span>
                <Trans
                  id="vehicle-lookup.create-item-error"
                  comment="Message shown when creating an ad fails, shown as an error message"
                >
                  Failed to create ad. Please try again.
                </Trans>
              </span>
            ) : submitError ? (
              <span id={variantErrorId}>
                <Trans
                  id="mobility-item-insertion.vehicle-variant.select-variant"
                  comment="Error message asking user to select a variant, with clickable link to scroll to variant selection"
                >
                  Select a{' '}
                  <Button
                    link
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    variant
                  </Button>{' '}
                  or choose 'Other / Don't know' if yours is not listed
                </Trans>
              </span>
            ) : undefined
          }
        />
      )}
      <Modal
        open={showConfirmModal}
        onDismiss={handleCancelUpdate}
        title={i18n.t({
          id: 'mobility-item-insertion.variant-selection.confirm-update.title',
          comment: 'Title for the confirmation modal when user changes variant',
          message: 'Change variant?',
        })}
      >
        <p className="mb-16">
          <Trans
            id="mobility-item-insertion.variant-selection.confirm-update.body"
            comment="Body text for the confirmation modal when user changes vehicle selection"
          >
            If you change the variant, we'll update your ad with the new details. Some information may be replaced.
          </Trans>
        </p>
        <div className="flex gap-8 justify-end">
          <Button type="button" onClick={handleCancelUpdate}>
            <Trans id="mobility-item-insertion.variant-selection.confirm-update.cancel">Cancel</Trans>
          </Button>
          <Button primary type="button" onClick={handleConfirmUpdate} loading={createOrUpdateMutation.isPending}>
            <Trans id="mobility-item-insertion.variant-selection.confirm-update.confirm">Change</Trans>
          </Button>
        </div>
      </Modal>
      <Modal
        open={showSearchAgainModal}
        onDismiss={handleCancelSearchAgain}
        title={i18n.t({
          id: 'mobility-item-insertion.variant-selection.search-again.title',
          comment: 'Title for the confirmation modal when user clicks "Search again"',
          message: 'Search again?',
        })}
      >
        <p className="mb-16">
          <Trans
            id="mobility-item-insertion.variant-selection.search-again.body"
            comment="Body text for the search again confirmation modal"
          >
            If you search again, we'll update your ad with the new vehicle details. Some information may be replaced.
          </Trans>
        </p>
        <div className="flex gap-8 justify-end">
          <Button type="button" onClick={handleCancelSearchAgain}>
            <Trans id="mobility-item-insertion.variant-selection.search-again.cancel">Cancel</Trans>
          </Button>
          <Button primary type="button" onClick={handleConfirmSearchAgain}>
            <Trans id="mobility-item-insertion.variant-selection.search-again.confirm">Search</Trans>
          </Button>
        </div>
      </Modal>
    </main>
  );
};
